import React, { createContext, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { resolveSounds } from './defaults';

// ============================================
// TYPES
// ============================================

/** All sound events the renderer can emit. */
export type SoundEvent =
  | 'drag-start'
  | 'drag-drop'
  | 'drag-cancel'
  | 'radio-select'
  | 'pair-select'
  | 'pair-link'
  | 'pair-unlink'
  | 'keypad-press'
  | 'matcher-select'
  | 'fraction-shade'
  | 'clock-hour-tick'
  | 'clock-minute-tick'
  | 'counter-step'
  | 'cursor-step'
  | 'cursor-release'
  | 'input-select'
  | 'answer-correct'
  | 'answer-wrong';

/**
 * Maps a sound event to either:
 *  - a `string` URL (renderer plays it via the native Audio API), or
 *  - a `() => void` callback (consumer owns playback — preloading, mobile
 *    unlock, volume, etc.).
 * Omitted events are silent.
 */
export type SoundConfig = Partial<Record<SoundEvent, string | (() => void)>>;

interface SoundContextValue {
  play: (event: SoundEvent) => void;
}

const SoundContext = createContext<SoundContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

export interface SoundProviderProps {
  children: React.ReactNode;
  /** Master on/off switch. When false, `play()` is a no-op. */
  enabled?: boolean;
  /**
   * Per-event overrides (URL or callback), merged over the bundled defaults.
   * Omit to use the built-in sounds for every event.
   */
  sounds?: SoundConfig;
}

/**
 * Builds a stable `play(event)` bound to the given config. Exposed separately
 * so a component can both share the player via context AND call it directly
 * (e.g. ExerciseRenderer firing `submit` / feedback sounds from its imperative
 * submit(), which runs outside the provider's own subtree).
 */
export function useSoundPlayer(
  enabled: boolean,
  sounds?: SoundConfig,
): (event: SoundEvent) => void {
  // Cache one Audio element per URL. Re-using avoids spawning a new element
  // (and a fresh network fetch/decode) on every play — important for rapid
  // events like keypad presses. Lazy creation also keeps the first play
  // inside the triggering user gesture, satisfying mobile autoplay rules.
  const audioCache = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Latest config in a ref so `play` keeps a stable identity (no consumer
  // re-renders) while still reading fresh values.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const resolved = useMemo(() => resolveSounds(sounds), [sounds]);
  const soundsRef = useRef<SoundConfig>(resolved);
  soundsRef.current = resolved;

  // Pre-warm: instantiate + buffer every URL ahead of the first interaction so
  // the first play isn't stalled by fetch/decode latency. Buffering (not
  // playing) outside a user gesture is allowed on mobile. Keyed on the URL set
  // so it re-runs only when the actual sources change.
  const urlKey = Object.values(resolved)
    .filter((v): v is string => typeof v === 'string')
    .join('|');
  useEffect(() => {
    const cache = audioCache.current;
    // Evict elements whose URL left the config so a runtime `sounds` swap
    // doesn't leak stale Audio entries forever.
    const liveUrls = new Set(
      Object.values(resolved).filter((v): v is string => typeof v === 'string'),
    );
    for (const url of [...cache.keys()]) {
      if (liveUrls.has(url)) continue;
      cache.get(url)?.pause();
      cache.delete(url);
    }
    if (!enabled) return;
    for (const url of liveUrls) {
      if (cache.has(url)) continue;
      try {
        const audio = new Audio(url);
        audio.preload = 'auto';
        audio.load();
        cache.set(url, audio);
      } catch {
        /* ignore — falls back to lazy create on play */
      }
    }
    // urlKey proxies the identity of `sounds` (via resolveSounds) so the effect
    // only re-runs when URLs actually change, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, enabled]);

  return useCallback((event: SoundEvent) => {
    if (!enabledRef.current) return;
    const entry = soundsRef.current?.[event];
    if (!entry) return;

    if (typeof entry === 'function') {
      entry();
      return;
    }

    // Never let audio failures break the triggering interaction (the caller's
    // onChange runs after play()). Guard construction, playback, and the
    // returned promise — jsdom's play() returns undefined, not a Promise.
    try {
      let audio = audioCache.current.get(entry);
      if (!audio) {
        audio = new Audio(entry);
        audio.preload = 'auto';
        audioCache.current.set(entry, audio);
      }
      // Reset so overlapping triggers replay from the start.
      audio.currentTime = 0;
      const result = audio.play();
      // play() rejects on autoplay block / interruption — swallow the promise
      // to avoid unhandled-rejection noise.
      if (result && typeof result.then === 'function') result.catch(() => {});
    } catch {
      /* audio unsupported or blocked — stay silent */
    }
  }, []);
}

export const SoundProvider: React.FC<SoundProviderProps> = ({
  children,
  enabled = true,
  sounds,
}) => {
  const play = useSoundPlayer(enabled, sounds);
  const value = useMemo<SoundContextValue>(() => ({ play }), [play]);
  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
};

/** Shares an already-built `play` over context (see useSoundPlayer). */
export const SoundPlayerProvider: React.FC<{
  play: (event: SoundEvent) => void;
  children: React.ReactNode;
}> = ({ play, children }) => {
  const value = useMemo<SoundContextValue>(() => ({ play }), [play]);
  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
};

// ============================================
// HOOK
// ============================================

/**
 * Returns `play(event)`. Safe to call outside a SoundProvider — it returns a
 * stable no-op, so widgets never need to know whether sound is wired up.
 */
export function useSound(): (event: SoundEvent) => void {
  const ctx = useContext(SoundContext);
  return ctx ? ctx.play : NOOP_PLAY;
}

const NOOP_PLAY = (_event: SoundEvent): void => {};
